/**
 * 전력·전력량 — 정격 전력별 세션 (DESIGN §8.9.1).
 *
 * **환경공단 충전이력(환경부 직접설치분)만이다.** 공개 API 에는 전력량이 없고, 전압·전류는
 * 어느 원천에도 없다 — 국가 통합연계 규격 자체에 정의가 없다(통합연계 가이드 v2.1). 그래서
 * 이 위젯은 범위를 제목에 박고, 좌측 필터와 무관하다고 적는다.
 *
 * 세션 **평균** 전력(kWh ÷ 시간)은 충전 후반 감속을 포함하므로 최대 출력보다 낮다 —
 * 「출력」이 아니라 「평균 충전전력」이라 부른다.
 */

import { useQuery } from '@tanstack/react-query'
import type { EChartsOption } from 'echarts'
import { useMemo, useState } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { EChart } from '@/components/EChart'
import { Headline } from '@/components/WidgetShell'
import { fetchEnvelope } from '@/lib/api'
import { axisStyle, CHART_H, rampAt, seqRamp } from '@/lib/chart'
import { cssVar } from '@/lib/cssvar'
import { int, pct } from '@/lib/fmt'
import type { Envelope, UsagePowerData, UsagePowerRow } from '@/lib/types'

type Metric = 'kw' | 'kwh' | 'mwh' | 'zero'

const METRICS: { key: Metric; label: string; axis: string; fmt: (v: number) => string }[] = [
  { key: 'kw', label: '평균 충전전력', axis: '세션 평균 전력의 월 중앙값 (kW)', fmt: (v) => `${v.toFixed(1)}kW` },
  { key: 'kwh', label: '세션당 충전량', axis: '세션 충전량의 월 중앙값 (kWh)', fmt: (v) => `${v.toFixed(1)}kWh` },
  { key: 'mwh', label: '월 충전량', axis: '월 충전량 합계 (MWh)', fmt: (v) => `${Math.round(v).toLocaleString('ko-KR')}MWh` },
  { key: 'zero', label: '0kWh 세션 비율', axis: '충전량 0 인 세션의 비율', fmt: (v) => `${(v * 100).toFixed(1)}%` },
]

function valueOf(r: UsagePowerRow, m: Metric): number | null {
  if (m === 'kw') return r.kw_p50
  if (m === 'kwh') return r.kwh_p50
  if (m === 'mwh') return r.mwh
  return r.zero_kwh_share
}

export function PowerProfile() {
  const q = useQuery({
    queryKey: ['usage/power'],
    queryFn: ({ signal }) =>
      fetchEnvelope<UsagePowerData>('/usage/power', { mode: 'standard' }, {}, signal),
    staleTime: 10 * 60 * 1000,
  })
  const env = q.data as Envelope<UsagePowerData> | undefined
  const d = env?.data.available ? env.data : null
  const [metric, setMetric] = useState<Metric>('kw')
  const m = METRICS.find((x) => x.key === metric) ?? METRICS[0]!

  // 세션이 적은 정격(30kW 등)은 선이 흔들린다 — 월 100건 미만 칸은 그리지 않는다
  const rows = useMemo(() => (d?.rows ?? []).filter((r) => r.sessions >= 100), [d])
  const ratings = useMemo(() => [...new Set(rows.map((r) => r.rated_kw))].sort((a, b) => a - b), [rows])
  const months = useMemo(() => [...new Set(rows.map((r) => r.ym))].sort(), [rows])
  // 정격은 순서형이라 명도로 나눈다 — 높은 정격일수록 짙다
  const colors = useMemo(() => seqRamp(ratings.length), [ratings.length])

  // 마지막 달은 부분월일 수 있다 — 표는 **마지막 완결월**을 쓴다
  const lastFull = useMemo(() => {
    const to = d?.meta.charge_to
    const cand = [...months].reverse()
    return cand.find((ym) => !to || !to.startsWith(ym)) ?? cand[0] ?? null
  }, [months, d])
  const snapshot = rows.filter((r) => r.ym === lastFull)

  const option = useMemo<EChartsOption>(() => {
    const ink3 = cssVar('--c-ink-3', '#626266')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    return {
      grid: { left: 60, right: 56, top: 20, bottom: 36 },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v) => (v == null ? '—' : m.fmt(Number(v))),
      },
      xAxis: { type: 'category', data: months, ...axisStyle(ink3, hairline) },
      yAxis: {
        type: 'value',
        ...axisStyle(ink3, hairline),
        axisLabel: {
          fontSize: 10,
          color: ink3,
          formatter: (v: number) =>
            metric === 'zero' ? `${Math.round(v * 100)}%` : metric === 'mwh' ? `${Math.round(v / 1000)}k` : String(v),
        },
      },
      series: ratings.map((kw, i) => {
        const by = new Map(rows.filter((r) => r.rated_kw === kw).map((r) => [r.ym, valueOf(r, metric)]))
        return {
          name: `${kw}kW`,
          type: 'line' as const,
          symbol: 'none',
          connectNulls: false,
          lineStyle: { width: 2, color: rampAt(colors, i) },
          itemStyle: { color: rampAt(colors, i) },
          data: months.map((ym) => by.get(ym) ?? null),
          endLabel: { show: true, fontSize: 10, color: ink3, formatter: `${kw}kW` },
        }
      }),
    }
  }, [rows, ratings, months, colors, metric, m])

  const top = snapshot.reduce<UsagePowerRow | null>((a, r) => (a == null || r.sessions > a.sessions ? r : a), null)

  return (
    <ChartFrame
      title="정격 전력별 충전 — 환경부 직접설치분"
      question="정격이 높을수록 실제로 더 빨리 충전되는가, 그리고 얼마나 충전되는가"
      meta={env?.meta}
      hideCoverage
      headline={
        top?.kw_p50 != null ? (
          <Headline value={`${top.kw_p50.toFixed(0)}kW`} unit={`${top.rated_kw}kW 급 평균 충전전력 · ${lastFull}`} />
        ) : undefined
      }
      legend={ratings.map((kw, i) => ({ label: `${kw}kW`, color: rampAt(colors, i), pattern: 'line' as const }))}
      controls={
        <div className="det__seg" role="group" aria-label="지표">
          {METRICS.map((x) => (
            <button
              key={x.key}
              type="button"
              className={`det__segbtn${metric === x.key ? ' is-on' : ''}`}
              aria-pressed={metric === x.key}
              onClick={() => setMetric(x.key)}
            >
              {x.label}
            </button>
          ))}
        </div>
      }
      encoding={[
        { role: '가로축', what: '월 (충전 시작 기준)' },
        { role: '세로축', what: m.axis },
        { role: '색 짙기', what: '정격 전력 — 높을수록 짙다' },
      ]}
      reading="환경공단 충전이력의 세션마다 충전량(kWh)과 시작·종료 시각이 있습니다. 평균 충전전력은 충전량 ÷ 충전 시간입니다."
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={!q.isLoading && rows.length === 0}
      emptyText={env && !env.data.available ? env.data.reason : '그릴 칸이 없습니다.'}
      onRetry={() => void q.refetch()}
      notes={[
        {
          label: '전압·전류는 없다.',
          text: '공개 API·환경공단 충전이력·국가 통합연계 규격 어디에도 전압·전류가 없습니다. 이 값은 충전기↔차량 통신과 사업자 서버(OCPP MeterValues)에만 있습니다.',
        },
        {
          label: '평균이지 최대가 아니다.',
          text: '충전 후반 감속(SOC 상승)이 들어 있어 정격보다 한참 낮은 것이 정상입니다. 차량의 수용 전력이 대부분을 정합니다.',
        },
        {
          label: '범위.',
          text: `환경부(ME) 직접설치분만이고 전국 52만 기가 아닙니다. 좌측 필터와 무관합니다. 원천 ${d?.meta.charge_from ?? '—'} ~ ${d?.meta.charge_to ?? '—'}, 세션 ${int(d?.meta.n_session)}건.`,
        },
        {
          label: '빼는 것.',
          text: '세션당 500kWh 초과는 계량 오버플로라 충전량 합산에서 뺍니다(세션 수에는 남김). 평균 전력은 5분 이상 세션만, 정격의 1.2배를 넘는 값(시각 오류)은 뺍니다.',
        },
      ]}
      table={
        <div className="ctable" tabIndex={0} role="region" aria-label="정격 전력별 충전 표">
          <table className="data-table">
            <caption className="sr-only">정격 전력별 충전 ({lastFull})</caption>
            <thead>
              <tr>
                <th scope="col">정격</th>
                <th scope="col">세션</th>
                <th scope="col">충전량</th>
                <th scope="col">평균 전력 중앙값</th>
                <th scope="col">상위 10%</th>
                <th scope="col">정격 대비</th>
                <th scope="col">세션당 kWh</th>
                <th scope="col">세션 시간</th>
                <th scope="col">0kWh 세션</th>
              </tr>
            </thead>
            <tbody>
              {snapshot.map((r) => (
                <tr key={r.rated_kw}>
                  <th scope="row">{r.rated_kw}kW</th>
                  <td className="num">{int(r.sessions)}</td>
                  <td className="num">{Math.round(r.mwh).toLocaleString('ko-KR')}MWh</td>
                  <td className="num">{r.kw_p50 != null ? `${r.kw_p50.toFixed(1)}kW` : '—'}</td>
                  <td className="num">{r.kw_p90 != null ? `${r.kw_p90.toFixed(1)}kW` : '—'}</td>
                  <td className="num">{pct(r.achievement, 0)}</td>
                  <td className="num">{r.kwh_p50 != null ? r.kwh_p50.toFixed(1) : '—'}</td>
                  <td className="num">{r.dur_min_p50 != null ? `${r.dur_min_p50.toFixed(0)}분` : '—'}</td>
                  <td className="num">{pct(r.zero_kwh_share, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="usage__tablefoot">{lastFull} 기준 (마지막 완결월)</p>
        </div>
      }
    >
      <EChart option={option} height={CHART_H} ariaLabel={`정격 전력별 ${m.label} 추이`} />
    </ChartFrame>
  )
}
